'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
}

interface StickyCartSummaryProps {
  cartItems?: CartItem[];
  onCheckout?: () => void;
  onViewCart?: () => void;
  className?: string;
}

const StickyCartSummary = ({
  cartItems = [],
  onCheckout = () => {},
  onViewCart = () => {},
  className = '',
}: StickyCartSummaryProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
        setIsExpanded(false);
      } else {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  if (cartItems.length === 0) {
    return null;
  }

  return (
    <>
      <div
        className={`fixed bottom-0 left-0 right-0 z-[150] transform transition-transform duration-250 ease-out lg:hidden ${
          isVisible ? 'translate-y-0' : 'translate-y-full'
        } ${className}`}
      >
        <div className="bg-card shadow-cta">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex w-full items-center justify-between px-6 py-4"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary">
                <Icon name="ShoppingCartIcon" size={20} className="text-primary-foreground" />
              </div>
              <div className="text-left">
                <div className="font-cta text-sm font-bold text-foreground">
                  {totalItems} {totalItems === 1 ? 'Item' : 'Items'}
                </div>
                <div className="text-xs text-muted-foreground">
                  ₹{totalPrice.toFixed(2)}
                </div>
              </div>
            </div>
            <Icon
              name={isExpanded ? 'ChevronDownIcon' : 'ChevronUpIcon'}
              size={24}
              className="text-foreground"
            />
          </button>

          {isExpanded && (
            <div className="border-t border-border px-6 py-4">
              <div className="mb-4 max-h-48 space-y-3 overflow-y-auto">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-3">
                    <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-muted">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-sm font-medium text-foreground">
                        {item.name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Qty: {item.quantity} × ₹{item.price.toFixed(2)}
                      </div>
                    </div>
                    <div className="text-sm font-bold text-primary">
                      ₹{(item.price * item.quantity).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={onViewCart}
                  className="flex-1 rounded-lg border-2 border-primary bg-background px-4 py-3 font-cta text-sm font-bold text-primary transition-colors duration-250 ease-out hover:bg-primary/10"
                >
                  View Cart
                </button>
                <button
                  onClick={onCheckout}
                  className="flex-1 rounded-lg bg-primary px-4 py-3 font-cta text-sm font-bold text-primary-foreground shadow-cta transition-all duration-250 ease-out hover:shadow-lg"
                >
                  Checkout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        className={`fixed right-6 top-24 z-[150] hidden w-80 rounded-lg bg-card shadow-cta lg:block ${className}`}
      >
        <div className="p-4">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-lg font-bold text-foreground">
              Your Cart
            </h3>
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary">
              <span className="font-cta text-sm font-bold text-primary-foreground">
                {totalItems}
              </span>
            </div>
          </div>

          <div className="mb-4 max-h-64 space-y-3 overflow-y-auto">
            {cartItems.map((item) => (
              <div key={item.id} className="flex items-center gap-3">
                <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-muted">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium text-foreground">
                    {item.name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Qty: {item.quantity} × ₹{item.price.toFixed(2)}
                  </div>
                </div>
                <div className="font-bold text-primary">
                  ₹{(item.price * item.quantity).toFixed(2)}
                </div>
              </div>
            ))}
          </div>

          <div className="mb-4 border-t border-border pt-4">
            <div className="flex items-center justify-between">
              <span className="font-heading text-lg font-bold text-foreground">
                Total
              </span>
              <span className="font-heading text-xl font-bold text-primary">
                ₹{totalPrice.toFixed(2)}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <button
              onClick={onCheckout}
              className="w-full rounded-lg bg-primary px-4 py-3 font-cta text-base font-bold text-primary-foreground shadow-cta transition-all duration-250 ease-out hover:shadow-lg"
            >
              Proceed to Checkout
            </button>
            <button
              onClick={onViewCart}
              className="w-full rounded-lg border-2 border-primary bg-background px-4 py-3 font-cta text-base font-bold text-primary transition-colors duration-250 ease-out hover:bg-primary/10"
            >
              View Full Cart
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default StickyCartSummary;